/**
 * Modal — LA modale, pour toutes les apps (lot 3 du catalogue, 28-10-2026 : retapée).
 *
 * Pourquoi une primitive : avant le 27-05-2026 chaque modale du cockpit (Transaction, Vault,
 * Panel, Poll, Settings…) dupliquait son `fixed inset-0` + fond + taille, avec des règles
 * mobiles incohérentes (l'une coupait l'en-tête, l'autre le pied, une troisième débordait).
 * Ici UNE mécanique (ouverture.ts) et UN dessin (ouverture.css) : le fond flou en verre, la
 * fenêtre en verre dépoli au grand arrondi, le titre en Almarena, l'entrée d'un glissement
 * court ; Échap et le fond ferment, le défilement de la page se verrouille, le focus entre
 * dans la fenêtre et revient d'où il venait, le Tab tourne en rond ; `100dvh` divisé par
 * `--zoom-affichage` (la taille d'affichage du cockpit) ; les encoches d'un iPhone absorbées.
 *
 *   <Modal isOpen={open} onClose={onClose} size="md" title="Programmer un message">
 *     <ModalBody>…ce qui défile…</ModalBody>
 *     <ModalFooter>…les boutons…</ModalFooter>
 *   </Modal>
 *
 * Un en-tête à soi (recherche, onglets) : pas de `title`, un `<ModalHeader>` enfant.
 *
 * Tailles : sm ~24 rem (confirmations, formulaires courts) · md ~32 rem (composeurs) · lg
 * ~44 rem (sélecteurs) · xl ~56 rem (galeries) · full (tout l'espace, à une marge près).
 * Sur mobile (< 640 px) : une feuille collée en bas, 85 % de la hauteur au plus.
 *
 * Portée : sur `document.body` en plein écran (Kayla, 09-09-2026 : « une modale qui se centre
 * dans un panneau de 300 px est une modale qu'on lit à la loupe »). `scope="panel"` garde
 * l'ancien ancrage dans le `cockpit-panel-body` du panneau hôte (PanelHostContext), pour le
 * jour où une modale VOUDRA ne couvrir qu'un panneau — aucun appelant ne le demande.
 *
 * La librairie ne dépend d'aucune app : l'étiquette de la croix est `closeLabel` (« Fermer »),
 * pas une clé i18n. `statique` la pose dans la page, sans fond ni portail — le catalogue.
 */
import { forwardRef, useCallback, useEffect, useRef, type CSSProperties, type MouseEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";

import { X } from "./icons";
import {
  Z_INDEX_CLASS,
  piegerLeTab,
  useCadreDuMarqueur,
  useFermerAEchap,
  useFocusDansLaFenetre,
  useVerrouDuDefilement,
  type ZIndexOuverture,
} from "./ouverture";
import { usePanelHost } from "./PanelHostContext";
import { useLibelles } from "./libelles";
import { cn } from "./utils";
import "./ouverture.css";

export type ModalSize = "sm" | "md" | "lg" | "xl" | "full";

export type ModalScope = "panel" | "viewport";

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Taille de la fenêtre. Défaut `md`. */
  size?: ModalSize;
  /** Un titre : l'en-tête standard, avec la croix. Pour un en-tête à soi, un `<ModalHeader>` enfant. */
  title?: string;
  /** Sous le titre, gris. */
  subtitle?: string;
  /** Pour les lecteurs d'écran. Défaut : `title`. */
  ariaLabel?: string;
  /** Défaut 400 (au-dessus du shell mobile du cockpit) ; 100 pour une sous-modale qui ne doit
   *  pas le recouvrir ; 500 pour passer au-dessus de tout (Confirmer). */
  zIndex?: ZIndexOuverture;
  /** `false` : un clic sur le fond ne ferme pas (pendant un envoi). */
  dismissOnBackdropClick?: boolean;
  /** `false` : Échap ne ferme pas (un assistant en plusieurs étapes). */
  dismissOnEscape?: boolean;
  /** `true` : ne verrouille pas le défilement de la page. */
  noBodyLock?: boolean;
  /** Défaut : l'écran. `panel` : le panneau hôte, s'il y en a un (cf docblock). */
  scope?: ModalScope;
  children: ReactNode;
  /** Sur la fenêtre. */
  className?: string;
  /** Style inline sur la fenêtre — les pages HORS shell qui surchargent les jetons `--color-*`
   *  sur leur racine (le parcours candidat de La Régie, thème clair forcé) : le portail sort de
   *  cette racine, la fenêtre doit re-porter les variables. */
  containerStyle?: CSSProperties;
  /** L'étiquette (lecteur d'écran) de la croix. Défaut « Fermer ». */
  closeLabel?: string;
  /** `alertdialog` pour ce qui attend une réponse (Confirmer). Défaut `dialog`. */
  role?: "dialog" | "alertdialog";
  /** Posée dans la page : ni fond, ni portail, ni verrou, ni focus — le catalogue, un test de rendu. */
  statique?: boolean;
}

const SIZE_TO_WIDTH: Record<ModalSize, string> = {
  sm: "w-full sm:max-w-sm",
  md: "w-full sm:max-w-md",
  lg: "w-full sm:max-w-2xl",
  xl: "w-full sm:max-w-4xl",
  full: "w-full sm:max-w-6xl",
};

export function Modal({
  isOpen,
  onClose,
  size = "md",
  title,
  subtitle,
  ariaLabel,
  zIndex = 400,
  dismissOnBackdropClick = true,
  dismissOnEscape = true,
  noBodyLock = false,
  scope,
  children,
  className,
  containerStyle,
  closeLabel,
  role = "dialog",
  statique = false,
}: ModalProps) {
  const { container: panelContainer } = usePanelHost();
  const fenetre = useRef<HTMLDivElement>(null);
  const marqueur = useRef<HTMLSpanElement>(null);

  // La prop explicite, sinon l'ÉCRAN (cf docblock — le panneau n'est plus jamais choisi tout seul).
  const effectiveScope: ModalScope = scope ?? "viewport";
  const isPanelScoped = effectiveScope === "panel" && panelContainer !== null;
  const vivante = isOpen && !statique;

  useFermerAEchap(vivante && dismissOnEscape, onClose);
  // Pas de verrou dans un panneau : son corps est déjà `overflow: hidden`, et les AUTRES panneaux
  // du shell doivent rester défilables.
  useVerrouDuDefilement(vivante && !noBodyLock && !isPanelScoped);
  useFocusDansLaFenetre(vivante, fenetre);
  const cadre = useCadreDuMarqueur(vivante && !isPanelScoped, marqueur);

  const handleBackdrop = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (!dismissOnBackdropClick) return;
      if (e.target === e.currentTarget) onClose();
    },
    [dismissOnBackdropClick, onClose],
  );

  if (!isOpen) return <span hidden ref={marqueur} />;
  if (typeof document === "undefined") return null;

  const contenu = (
    <>
      {title && <ModalHeader title={title} subtitle={subtitle} onClose={onClose} closeLabel={closeLabel} />}
      {children}
    </>
  );

  if (statique) {
    return (
      <div
        role={role}
        aria-label={ariaLabel ?? title ?? undefined}
        style={containerStyle}
        className={cn("ov-fenetre ov-arrondi cockpit-modal", SIZE_TO_WIDTH[size], className)}
      >
        {contenu}
      </div>
    );
  }

  const fenetreProps = {
    ref: fenetre,
    tabIndex: -1,
    onKeyDown: piegerLeTab,
    onClick: (e: MouseEvent) => e.stopPropagation(),
    style: containerStyle,
    "data-entree": "modale",
  } as const;

  // Dans le panneau hôte : `absolute inset-0`, un centrage simple (un panneau peut être très
  // étroit, la feuille du bas n'a pas de sens), la fenêtre jamais plus large que lui.
  if (isPanelScoped) {
    return createPortal(
      <div
        role={role}
        aria-modal="true"
        aria-label={ariaLabel ?? title ?? undefined}
        onClick={handleBackdrop}
        className={cn("ov-fond cockpit-modal-fond absolute inset-0 flex items-center justify-center p-2", Z_INDEX_CLASS[zIndex])}
      >
        <div {...fenetreProps} className={cn("ov-fenetre ov-arrondi cockpit-modal max-h-full max-w-full", SIZE_TO_WIDTH[size], className)}>
          {contenu}
        </div>
      </div>,
      panelContainer,
    );
  }

  // Sur l'écran : `fixed inset-0`, la feuille collée en bas sur mobile (`items-end`), centrée
  // au-delà. `85dvh` sur mobile laisse voir qu'on est encore sur l'écran ; divisé par le zoom
  // d'affichage, parce que `dvh` l'ignore (cf globals.css du cockpit). Le marqueur reste À SA
  // PLACE dans l'arbre : c'est là que se lit le thème recopié sur l'enveloppe du portail.
  return (
    <>
      <span hidden ref={marqueur} />
      {createPortal(
    <div className={cadre.sombre ? "dark" : undefined} data-theme={cadre.theme}>
      <div
        role={role}
        aria-modal="true"
        aria-label={ariaLabel ?? title ?? undefined}
        onClick={handleBackdrop}
        data-mise="modale"
        className={cn("ov-fond cockpit-modal-fond fixed inset-0 flex items-end justify-center sm:items-center", Z_INDEX_CLASS[zIndex])}
      >
        <div
          {...fenetreProps}
          className={cn(
            "ov-fenetre ov-arrondi-feuille cockpit-modal max-h-[calc(85dvh/var(--zoom-affichage,1))] sm:max-h-[calc(100dvh/var(--zoom-affichage,1)-2rem)]",
            SIZE_TO_WIDTH[size],
            className,
          )}
        >
          {contenu}
        </div>
      </div>
    </div>,
        document.body,
      )}
    </>
  );
}

export interface ModalHeaderProps {
  /** Le titre. Sans lui ni enfants : la croix seule. */
  title?: string;
  /** Sous le titre, gris. */
  subtitle?: string;
  /** Rend la croix. Sans lui : pas de croix (un en-tête à soi). */
  onClose?: () => void;
  /** La croix inactive (pendant un envoi). */
  closeDisabled?: boolean;
  /** L'étiquette (lecteur d'écran) de la croix. Défaut « Fermer ». */
  closeLabel?: string;
  /** À la place du titre : du contenu libre (recherche, onglets). La croix reste à droite. */
  children?: ReactNode;
  className?: string;
}

/** L'en-tête : le titre à gauche, la croix à droite ; il ne défile pas. */
export function ModalHeader({
  title,
  subtitle,
  onClose,
  closeDisabled = false,
  closeLabel,
  children,
  className,
}: ModalHeaderProps) {
  const libelles = useLibelles();
  return (
    <div className={cn("ov-tete", className)}>
      {children ? (
        <div className="min-w-0 flex-1">{children}</div>
      ) : (
        <div className="min-w-0 flex-1">
          {title && <div className="ov-titre">{title}</div>}
          {subtitle && <div className="ov-sous-titre">{subtitle}</div>}
        </div>
      )}
      {onClose && (
        <button type="button" onClick={onClose} disabled={closeDisabled} aria-label={closeLabel ?? libelles.fermer} className="ov-croix ov-rond">
          <X />
        </button>
      )}
    </div>
  );
}

export interface ModalBodyProps {
  children: ReactNode;
  className?: string;
  /** Les marges intérieures. Défaut `px-4 py-4` ; une chaîne vide les retire. */
  padding?: string;
}

/**
 * Le corps : ce qui défile, entre l'en-tête et le pied. `forwardRef` expose le div qui défile
 * à un contenu virtualisé (react-virtual, `getScrollElement` — cf VaultPicker).
 */
export const ModalBody = forwardRef<HTMLDivElement, ModalBodyProps>(function ModalBody(
  { children, className, padding = "px-4 py-4" },
  ref,
) {
  return (
    <div ref={ref} className={cn("ov-corps", padding, className)}>
      {children}
    </div>
  );
});

export interface ModalFooterProps {
  children: ReactNode;
  className?: string;
}

/** Le pied : les boutons, à droite ; il ne défile pas et n'est jamais coupé sur mobile. */
export function ModalFooter({ children, className }: ModalFooterProps) {
  return <div className={cn("ov-pied", className)}>{children}</div>;
}

/**
 * Le focus sur le premier champ au montage — pose la ref sur l'input visé. `requestAnimationFrame`
 * attend que le portail soit vraiment dans le DOM et visible, sinon `focus()` ne fait rien.
 */
export function useAutoFocusOnMount<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const id = requestAnimationFrame(() => el.focus());
    return () => cancelAnimationFrame(id);
  }, []);
  return ref;
}
